/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Download, FileDownload } from '@mui/icons-material';
import { useLang } from '~/hooks/useLang';
import { exportDispatches } from '~/services/statistics';
import { IExportDispatchesFilters } from '~/interfaces/IStatistics';
import notify from '~/utils/notify';
import { generateCSVFileName } from '~/utils/strings';

interface ExportDispatchesButtonProps {
  readonly filters: Omit<IExportDispatchesFilters, 'typeFile'>;
  readonly disabled?: boolean;
}

export default function ExportDispatchesButton({
  filters,
  disabled = false,
}: ExportDispatchesButtonProps) {
  const { translate } = useLang();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
  };

  const handleExport = async (typeFile: 'csv' | 'kml' | 'geojson' | 'xml') => {
    handleClose();
    setLoading(true);

    try {
      const blob = await exportDispatches({
        ...filters,
        typeFile,
      });

      const filename = generateCSVFileName('Dispatches', translate).replace('.csv', `.${typeFile}`);
      
      downloadFile(blob, filename);

      notify({
        message: translate('File exported successfully'),
        type: 'success',
        autoCloseDuration: 3000,
      });
    } catch (error) {
      console.error('Error exporting dispatches:', error);
      notify({
        message: translate('Error exporting file'),
        type: 'error',
        autoCloseDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
        onClick={handleClick}
        disabled={disabled || loading}
      >
        {translate('Export Data')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translate('Export as CSV')} />
        </MenuItem>
        <MenuItem onClick={() => handleExport('kml')}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translate('Export as KML')} />
        </MenuItem>
        <MenuItem onClick={() => handleExport('geojson')}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translate('Export as GeoJSON')} />
        </MenuItem>
        <MenuItem onClick={() => handleExport('xml')}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translate('Export as XML')} />
        </MenuItem>
      </Menu>
    </>
  );
}
